import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireSession } from './components/RequireSession'
import { AddAppliancePage } from './pages/AddAppliancePage'
import { AdminAddDevicePage } from './pages/AdminAddDevicePage'
import { AdminAlertWhitelistPage } from './pages/AdminAlertWhitelistPage'
import { AdminBillingPage } from './pages/AdminBillingPage'
import { AdminBrpsPage } from './pages/AdminBrpsPage'
import { AdminBrpNotificationsPage } from './pages/AdminBrpNotificationsPage'
import { AdminReservePayoutsPage } from './pages/AdminReservePayoutsPage'
import { AdminPayoutPartnersPage } from './pages/AdminPayoutPartnersPage'
import { AdminDevicePage } from './pages/AdminDevicePage'
import { AdminDevicesPage } from './pages/AdminDevicesPage'
import { AdminDevOpsPage } from './pages/AdminDevOpsPage'
import { AdminQuickFinderPage } from './pages/AdminQuickFinderPage'
import { AdminTelemetryPage } from './pages/AdminTelemetryPage'
import { AdminUserGroupsPage } from './pages/AdminUserGroupsPage'
import { AdminUserPage } from './pages/AdminUserPage'
import { AdminUsersPage } from './pages/AdminUsersPage'
import { AppliancesPage } from './pages/AppliancesPage'
import { CIControlPage } from './pages/CIControlPage'
import { ClaimPage } from './pages/ClaimPage'
import { ConfigureAppliancePage } from './pages/ConfigureAppliancePage'
import { DebugPage } from './pages/DebugPage'
import { DeviceConfigPage } from './pages/DeviceConfigPage'
import { DevicePage } from './pages/DevicePage'
import { DevicesPage } from './pages/DevicesPage'
import { DiagnosticHelpPage } from './pages/DiagnosticHelpPage'
import { EmptyPage } from './pages/EmptyPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { HistoryPage } from './pages/HistoryPage'
import { HomePage } from './pages/HomePage'
import { IndexPage } from './pages/IndexPage'
import { LoginPage } from './pages/LoginPage'
import { NewPasswordPage } from './pages/NewPasswordPage'
import { ProfitsPage } from './pages/ProfitsPage'
import { RadioConfigPage } from './pages/RadioConfigPage'
import { RegisterPage } from './pages/RegisterPage'
import { SaveConfigPage } from './pages/SaveConfigPage'
import { SettingsPage } from './pages/SettingsPage'
import { TestAppliancePage } from './pages/TestAppliancePage'
import { VerifyPage } from './pages/VerifyPage'
import { WidgetsPage } from './pages/WidgetsPage'

/**
 * The route table.
 *
 * Only the routes listed first are reachable without a session. They are the ones a
 * person needs *in order to get* a session: the entry redirect, login, registration,
 * the password-reset request, and the two pages opened from a mailed link (password
 * reset, email verification / invitation) — a mailed link opens in a browser that has
 * no token, so bouncing those to login would break account creation and password
 * reset. `/ci` is the CI control page, which uses no session data and is already
 * listed as unauthenticated in `Safety`.
 *
 * Every other route is nested under `RequireSession` and redirects to `/login` before
 * the page renders when there is no user in the session. This includes the wildcard,
 * so an unknown deep link never renders a page that assumes session data.
 */
export function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      {/* Public: reachable without a session. Keep this list short and deliberate. */}
      <Route path="/" element={<IndexPage/>}/>
      <Route path="/ci" element={<CIControlPage/>}/>
      <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
      <Route path="/login" element={<LoginPage/>}/>
      <Route path="/new-password/:token" element={<NewPasswordPage/>}/>
      <Route path="/register" element={<RegisterPage/>}/>
      <Route path="/verify/:token" element={<VerifyPage/>}/>
      <Route path="/verify/:token/:password" element={<VerifyPage/>}/>

      {/* Everything else requires a session. */}
      <Route element={<RequireSession/>}>
        <Route path="/admin/alert-whitelist" element={<AdminAlertWhitelistPage/>}/>
        <Route path="/admin/billing" element={<AdminBillingPage/>}/>
        <Route path="/admin/brps" element={<AdminBrpsPage/>}/>
        <Route path="/admin/brp-notifications" element={<AdminBrpNotificationsPage/>}/>
        <Route path="/admin/devices" element={<AdminDevicesPage/>}/>
        <Route path="/admin/devices/:id" element={<AdminDevicePage/>}/>
        <Route path="/admin/devices/add" element={<AdminAddDevicePage/>}/>
        <Route path="/admin/quick-finder" element={<AdminQuickFinderPage/>}/>
        <Route path="/admin/reserve-payouts" element={<AdminReservePayoutsPage/>}/>
        <Route path="/admin/payout-partners" element={<AdminPayoutPartnersPage/>}/>
        <Route path="/admin/users" element={<AdminUsersPage/>}/>
        <Route path="/admin/users/:id" element={<AdminUserPage/>}/>
        <Route path="/admin/user-groups" element={<AdminUserGroupsPage/>}/>
        <Route path="/admin/user-groups/:id" element={<AdminUserGroupsPage/>}/>
        <Route path="/admin/telemetry" element={<AdminTelemetryPage/>}/>
        <Route path="/admin/devops" element={<AdminDevOpsPage/>}/>
        <Route path="/appliances" element={<AppliancesPage/>}/>
        <Route path="/appliances/:applianceId" element={<ConfigureAppliancePage/>}/>
        <Route path="/appliances/new" element={<AddAppliancePage/>}/>
        <Route path="/appliances/new/:type" element={<AddAppliancePage/>}/>
        <Route path="/appliances/new/:type/:subtype" element={<AddAppliancePage/>}/>
        <Route path="/appliances/new/:type/:subtype/:portId" element={<ConfigureAppliancePage/>}/>
        <Route path="/appliances/test/:widgetId" element={<TestAppliancePage/>}/>
        <Route path="/buy" element={<EmptyPage/>}/>
        <Route path="/claim" element={<ClaimPage/>}/>
        <Route path="/config/:id" element={<DeviceConfigPage/>}/>
        <Route path="/config/:id/:portId" element={<DeviceConfigPage/>}/>
        <Route path="/contact" element={<EmptyPage/>}/>
        <Route path="/debug" element={<DebugPage/>}/>
        <Route path="/devices" element={<DevicesPage/>}/>
        <Route path="/devices/:id" element={<DevicePage/>}/>
        <Route path="/history" element={<HistoryPage/>}/>
        <Route path="/profits" element={<ProfitsPage/>}/>
        <Route path="/help/diagnostics" element={<DiagnosticHelpPage/>}/>
        <Route path="/help/diagnostics/:checkType" element={<DiagnosticHelpPage/>}/>
        <Route path="/home" element={<HomePage/>}/>
        <Route path="/learn" element={<EmptyPage/>}/>
        <Route path="/notes" element={<EmptyPage/>}/>
        <Route path="/radio/:id" element={<RadioConfigPage/>}/>
        <Route path="/save/:id" element={<SaveConfigPage/>}/>
        <Route path="/save/:id/:portId" element={<SaveConfigPage/>}/>
        <Route path="/settings" element={<SettingsPage/>}/>
        <Route path="/widgets" element={<WidgetsPage/>}/>
        {/* Unknown URL: with a session, let the index page pick the landing page. */}
        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Route>
    </Routes>
  )
}
